'use client';

import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { CornerBrackets } from '../ui/CornerBrackets';

interface LaunchLayoutProps {
  form: ReactNode;
  /** Omit to render the form full-width (used on the Review step). */
  preview?: ReactNode;
  /** Optional tiny slot top-left of the frame (e.g. step counter). */
  eyebrow?: ReactNode;
  /** Optional slot top-right of the frame (e.g. close button). */
  action?: ReactNode;
  /**
   * Review-mode: disable internal scroll on the form column and let the step
   * self-size so the content stays within a single viewport. Pairs with the
   * ReviewStep's own flex layout.
   */
  compact?: boolean;
}

/**
 * Single window Frame — replaces the previous header-ribbon + two-column layout.
 * Form lives on the left, live preview on the right. Both share one bordered
 * container with corner brackets, matching the lobby aesthetic.
 *
 * The frame itself doesn't scroll; the inner form column does. The preview
 * sticks to the frame's top so it stays visible while the form scrolls.
 */
export function LaunchLayout({ form, preview, eyebrow, action, compact }: LaunchLayoutProps) {
  return (
    <div
      className={cn(
        'relative mx-auto w-full max-w-[1280px]',
        'rounded-[4px] border border-amber-400/25',
        'bg-gradient-to-b from-[#120e08]/85 via-[#0c0a06]/90 to-[#0a0805]/95',
        'shadow-[0_30px_120px_-40px_rgba(0,0,0,0.9),0_0_0_1px_rgba(255,200,100,0.05)_inset]',
        'backdrop-blur-md',
      )}
    >
      <CornerBrackets size={18} color="rgba(255, 200, 100, 0.65)" />

      {(eyebrow || action) && (
        <header
          className={cn(
            'flex items-center justify-between gap-4 border-b border-amber-400/10 px-6 py-3',
            'text-[11px] uppercase tracking-[0.3em] text-amber-100/50',
          )}
        >
          <div className="truncate">{eyebrow}</div>
          {action ? <div className="shrink-0">{action}</div> : null}
        </header>
      )}

      <div
        className={cn(
          'relative flex flex-col gap-6',
          preview
            ? 'p-4 md:grid md:grid-cols-[minmax(0,1fr)_minmax(360px,520px)] md:gap-8 md:p-8 lg:grid-cols-[minmax(0,640px)_minmax(420px,1fr)]'
            : compact
              // Compact (Review) mode: the form column paints its own
              // ambient backdrop that needs to reach the frame border.
              // Drop the container padding so the skybox bleeds edge to
              // edge; ReviewStep carries its own internal padding.
              ? ''
              : 'p-4 md:p-8',
        )}
      >
        {/* Form column.
              Mobile: no internal scroll cap — the wizard's outer
                `overflow-y-auto` is the single scroll surface, so the form
                flows naturally and the LaunchFooter (back/continue) stays
                pinned above the terms strip without nested scrollers
                fighting each other. Previously a `max-h-[min(72vh,760px)]`
                trapped the form inside the layout frame on small phones,
                cutting off the last yield-source rows behind the bottom
                chrome and forcing users to guess where Continue lived.
              Desktop (`md:`): keep the bordered scroll area so the
                preview column can stay sticky alongside it.
              Review (`compact`): no scroll either way — the step paints
                its own ambient backdrop and self-sizes within the
                viewport. */}
        <section
          data-gild-scroll
          className={cn(
            'relative flex flex-col',
            compact
              // No side padding in compact so the ambient sky wash runs
              // to the frame border. Bottom corners clip the wash so the
              // frame keeps its rounded silhouette.
              ? 'overflow-hidden rounded-b-[3px] h-[min(72vh,640px)]'
              : 'md:pr-1 md:overflow-y-auto md:max-h-[min(72vh,760px)]',
          )}
        >
          <div className={cn('w-full', compact ? 'h-full' : 'pb-2')}>{form}</div>
        </section>

        {preview ? (
          <aside className="relative hidden md:block">
            <div className="sticky top-0">{preview}</div>
          </aside>
        ) : null}
      </div>
    </div>
  );
}
